import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { FontAwesomeIcon } from '../components/Icon';
import { faArrowLeft, faTrashCan, faLock, faCartShopping } from '@fortawesome/free-solid-svg-icons';
import { useCart, useDialog, useQuote, useSettings } from '../lib/store';
import { useI18n } from '../lib/i18n';
import { CouponForm, Totals } from '../components/OrderSummary';
import { Empty, PageHead, QtyInput } from '../components/ui';
import { imgUrl } from '../lib/img';

export default function Cart() {
  const { money } = useSettings();
  const { t, pick, isAr } = useI18n();
  const cart = useCart();
  const dialog = useDialog();
  const { quote, loading } = useQuote();
  useEffect(() => { document.title = t('Your cart'); }, []);

  // Drop items the server no longer knows about (deleted / hidden products).
  useEffect(() => {
    if (!quote || loading) return;
    const known = new Set(quote.lines.map((l) => l.id));
    if (cart.items.some((i) => !known.has(i.id))) cart.keepOnly(known);
  }, [quote, loading]);

  const head = <PageHead title={t('Your cart')} crumbs={[[t('Home'), '/'], [t('Cart')]]} />;
  if (!cart.items.length) {
    return <>{head}<div className="shell py-12"><Empty><FontAwesomeIcon icon={faCartShopping} className="mb-3 text-3xl text-slate-300" /><br />{t('Your cart is empty.')}<br /><Link to="/shop" className="btn btn-primary mt-5">{t('Start shopping')}</Link></Empty></div></>;
  }
  if (!quote) return <>{head}<div className="shell py-10"><div className="skeleton h-72" /></div></>;

  const errorFor = (id) => quote.errors.filter((e) => e.id === id);
  const errText = (e) => t(e.errorKey || e.error, { ...e.vars, name: isAr && e.vars?.nameAr ? e.vars.nameAr : e.vars?.name });

  return (
    <>
      {head}
      <div className="shell grid items-start gap-8 pt-8 pb-16 lg:grid-cols-[minmax(0,1fr)_380px]">
        <div className={`panel transition-opacity ${loading ? 'opacity-60' : ''}`}>
          <ul className="divide-y divide-slate-200">
            {quote.lines.map((l) => (
              <li key={l.id} className="grid grid-cols-[64px_1fr] items-center gap-4 py-4 sm:grid-cols-[84px_1fr_auto]">
                <Link to={`/product/${l.slug}`}><img src={imgUrl(l.image || '/uploads/cable.svg', 192)} loading="lazy" alt="" className="size-16 rounded-xl bg-soft object-cover sm:size-21" /></Link>
                <div className="min-w-0">
                  <h2 className="font-bold"><Link to={`/product/${l.slug}`} className="hover:text-brand-600"><bdi>{pick(l, 'name')}</bdi></Link></h2>
                  <p className="mt-0.5 mb-2 text-[13px] text-slate-500">
                    {t('{price} each', { price: money(l.salePrice) })}{' '}
                    {l.discountPercent > 0 && <><s>{money(l.unitPrice)}</s> <span className="font-bold text-sale" dir="ltr">-{l.discountPercent}%</span></>}
                  </p>
                  <div className="flex items-center gap-4">
                    <QtyInput value={l.qty} onChange={(q) => cart.setQty(l.id, q)} />
                    <button onClick={() => cart.remove(l.id)} className="cursor-pointer text-[13px] text-slate-500 hover:text-sale">
                      <FontAwesomeIcon icon={faTrashCan} /> {t('Remove')}
                    </button>
                  </div>
                  {errorFor(l.id).map((e) => <p key={e.error} className="mt-1.5 text-[13px] text-sale">{errText(e)}</p>)}
                </div>
                <div className="col-start-2 font-extrabold sm:col-start-auto sm:text-end">{money(l.lineTotal)}</div>
              </li>
            ))}
          </ul>
          <div className="mt-4 flex flex-wrap justify-between gap-3">
            <Link to="/shop" className="btn btn-outline btn-sm"><FontAwesomeIcon icon={faArrowLeft} /> {t('Continue shopping')}</Link>
            <button className="cursor-pointer text-[13px] text-slate-500 underline hover:text-sale"
              onClick={async () => { if (await dialog.confirm(t('Remove all items from your cart?'), { title: t('Empty cart?'), confirmLabel: t('Empty cart'), danger: true })) cart.clear(); }}>{t('Empty cart')}</button>
          </div>
        </div>

        <aside className="panel lg:sticky lg:top-24">
          <h2 className="mb-4 text-lg font-bold">{t('Order summary')}</h2>
          <CouponForm totals={quote.totals} />
          <Totals t={quote.totals} />
          {quote.errors.length
            ? <button className="btn btn-primary mt-4 w-full" disabled>{t('Fix cart issues to continue')}</button>
            : <Link to="/checkout" className="btn btn-primary mt-4 w-full"><FontAwesomeIcon icon={faLock} /> {t('Proceed to checkout')}</Link>}
        </aside>
      </div>
    </>
  );
}
